import React, { useContext, useRef } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';
import { Link, navigate } from 'gatsby';
import { useTranslation } from './useTranslation';
import validationRules, {
  BUSINESS_REGISTRATION_TYPE,
  Model,
  RegistrationPageStep,
} from '../components/registration/registration.rules';
import {
  registrationOnlineUserMutation,
  updatePersonIdValueDetailsMutation,
  validatePersonForRegistrationQuery,
} from '../queries/registration.query';
import ROUTES from '../routes';
import { formatSocialSecurity, parsePhoneNumber } from '../util/format';
import useFormState from './useFormState';
import {
  MutationRegisterOnlineUserArgs,
  MutationUpdatePersonIdValueDetailsArgs,
  OnlineRegistrationType,
  PersonPrimaryIdType,
  QueryValidatePersonForRegistrationArgs,
  RegistrationResponse,
  UpdatePersonIdValueDetailsResponse,
  ValidatePersonForRegistrationResponse,
} from '../__generated__/pge-types';
import { TransitionContext } from '../providers/TransitionProvider';
import useAuth from './useAuth';
import { NotificationsContext } from '../providers/NotificationsProvider';
import usePgeQuery from './usePgeQuery';
import moment from 'moment';

export const RegistrationErrors = Object.freeze({
  ProvidedAccountNotFound: 1,
  ProvidedPhoneOrEmailNotFound: 2,
  NoPersonsFound: 3,
  SpecialHandling: 4,
  AccountAlreadyRegistered: 5,
  WebRegisteredEmailFound: 6,
  AccountLocked: 7,
  InvalidBusinessInformation: 8,
  UsernameInUse: 9,
});

export const defaultFormValues: Model = {
  registrationType: OnlineRegistrationType.Residential,
  email: '',
  password: '',
  phoneNumber: '',
  accountNumber: '',
  pinCode: '',
  primaryAccountOption: 'accountNumber',
  firstName: '',
  middleName: '',
  lastName: '',
  birthdate: '',
  IDType: PersonPrimaryIdType.Ssn,
  IDState: 'OR',
  IDValue: '',
  step: RegistrationPageStep.INFO,
  availableExistingIDTypes: [],
  businessRegistrationType: BUSINESS_REGISTRATION_TYPE.BusinessEntity,
  businessName: '',
  existingIDType: undefined,
  existingIDValue: '',
};

type BasicRegistrationData = Pick<
  Model,
  | 'email'
  | 'password'
  | 'phoneNumber'
  | 'accountNumber'
  | 'pinCode'
  | 'IDType'
  | 'IDValue'
>;

export default function useRegistrationForm(
  defaultAccountNumber?: string,
  redirectUrl?: string,
) {
  const { signInWithPassword } = useAuth();
  const form = useFormState(
    {
      ...defaultFormValues,
      accountNumber: defaultAccountNumber || '',
    },
    { validate: validationRules },
  );
  const { setState: updateTransition } = useContext(TransitionContext);
  const errorCount = useRef(0);
  const { t, richT } = useTranslation();
  const notificationContext = useContext(NotificationsContext);

  const handleError = (errorMessage: any) => {
    notificationContext.setState({
      isOpen: true,
      message: errorMessage,
      severity: 'error',
    });
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const onError = (err: number) => {
    updateTransition({ open: false });

    errorCount.current += 1;
    if (errorCount.current > 2) {
      return navigate(ROUTES.REGISTRATION_SPECIAL_HANDLING);
    }

    switch (err) {
      case RegistrationErrors.ProvidedAccountNotFound:
        handleError(t('REGISTRATION_ACCOUNT_DETAILS_NOT_FOUND'));
        break;
      case RegistrationErrors.ProvidedPhoneOrEmailNotFound:
        handleError(t('REGISTRATION_ACCOUNT_DETAILS_NOT_FOUND'));
        break;
      case RegistrationErrors.NoPersonsFound:
        handleError(t('REGISTRATION_ACCOUNT_DETAILS_NOT_FOUND'));
        break;
      case RegistrationErrors.SpecialHandling:
        return navigate(ROUTES.REGISTRATION_SPECIAL_HANDLING);
      case RegistrationErrors.AccountAlreadyRegistered:
      case RegistrationErrors.UsernameInUse:
        handleError(
          <span>
            {t('ALREADY_REGISTERED_1')}{' '}
            <Link
              css={{ textDecoration: 'underline !important' }}
              to={ROUTES.SIGN_IN}
            >
              {t('SIGN_IN')}
            </Link>{' '}
            {t('ALREADY_REGISTERED_2')}
          </span>,
        );
        break;
      case RegistrationErrors.WebRegisteredEmailFound:
        form.setError(
          'email',
          <span>
            {t('ALREADY_REGISTERED_SERVICES_1')}{' '}
            <Link to={ROUTES.SIGN_IN}>{t('SIGN_IN')}</Link>{' '}
            {t('ALREADY_REGISTERED_SERVICES_2')}
          </span>,
        );
        void form.setValue('step', RegistrationPageStep.INFO);
        break;
      case RegistrationErrors.AccountLocked:
        handleError(
          richT('REGISTRATION_ATTEMPTS_EXCEEDED_DAILY_LIMIT', {
            CUSTOMER_SERVICE_NUMBER: `tel:${t('CALL_FOR_ASSISTANCE_NUMBER')}`,
          }),
        );
        break;
      case RegistrationErrors.InvalidBusinessInformation:
        handleError(richT('COMMERCIAL_INVALID_BUSINESS_INFORMATION'));
        break;
      default:
        showCallForAssitanceBanner();
    }
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const onSuccess = async (uid: string, email: string, password: string) => {
    try {
      updateTransition({ open: true });
      const signInResult = await signInWithPassword(
        email.toLowerCase(),
        password,
      );
      if (signInResult.status !== 'SUCCESS') {
        updateTransition({ open: false });
        return showCallForAssitanceBanner();
      }
    } catch (error) {
      updateTransition({ open: false });
      return showCallForAssitanceBanner();
    }
    await updateTransition({ open: false });

    if (redirectUrl) {
      notificationContext.setState({
        isOpen: true,
        message: t('REGISTRATION_COMPLETE_SUBTITLE'),
        severity: 'success',
      });

      return navigate(redirectUrl);
    }

    const groupName = createGroupName(form.values);

    return navigate(ROUTES.REGISTRATION_COMPLETE, {
      state: {
        uid,
        email,
        groupName,
      },
      replace: true,
    });
  };

  const { refetch: validatePersonForRegistration, loading } = usePgeQuery<
    { validatePersonForRegistration: ValidatePersonForRegistrationResponse },
    QueryValidatePersonForRegistrationArgs
  >(validatePersonForRegistrationQuery, {
    skip: true,
  });
  const isUserWithoutExistingID =
    form.values.availableExistingIDTypes?.length === 0;
  const isNewIDRegistration =
    form.values.existingIDType === PersonPrimaryIdType.None;

  const getIdDetails = () => {
    if (isUserWithoutExistingID || isNewIDRegistration) {
      return {
        VerificationType: form.values.IDType,
        VerificationValue: form.values.IDValue.slice(-4),
      };
    }

    return {
      VerificationType: form.values.existingIDType!,
      VerificationValue: form.values.existingIDValue.slice(-4),
    };
  };

  const [registerIDDetails] = useMutation<
    { updatePersonIdValueDetails: UpdatePersonIdValueDetailsResponse },
    MutationUpdatePersonIdValueDetailsArgs
  >(updatePersonIdValueDetailsMutation);

  const [registrationOnlineUser] = useMutation<
    { registerOnlineUser: RegistrationResponse },
    MutationRegisterOnlineUserArgs
  >(registrationOnlineUserMutation, {
    variables: {
      payload: {
        registrationType: form.values.registrationType,
        AccountNumber:
          form.values.primaryAccountOption === 'accountNumber'
            ? form.values.accountNumber
            : '',
        EmailAddress: form.values.email,
        Password: form.values.password,
        PhoneNumber:
          form.values.primaryAccountOption === 'phoneNumber'
            ? parsePhoneNumber(form.values.phoneNumber)
            : '',
        PinCode: form.values.pinCode,
        ...getIdDetails(),
      },
    },
    errorPolicy: 'all',
    onError: () => {
      updateTransition({ open: false });
      return showCallForAssitanceBanner();
    },
    onCompleted: response => {
      if (response?.registerOnlineUser?.success) {
        const { uid } = response.registerOnlineUser;
        return onSuccess(uid!, form.values.email, form.values.password);
      } else if (response?.registerOnlineUser?.error) {
        void onError(response?.registerOnlineUser?.error);
      } else {
        updateTransition({ open: false });
        return showCallForAssitanceBanner();
      }
    },
  });

  const handleSubmit = () => {
    return handleRegistration();
  };

  const handleRegistrationWithUserData = async (
    data: BasicRegistrationData,
  ) => {
    updateTransition({ open: true });

    try {
      await form.setValue('accountNumber', data?.accountNumber);
      await form.setValue('email', data?.email);
      await form.setValue('phoneNumber', data?.phoneNumber);
      await form.setValue('pinCode', data.pinCode);
      await form.setValue('IDType', data?.IDType);
      await form.setValue('IDValue', data?.IDValue); //last 4 digits
      await registrationOnlineUser();
    } catch (e) {
      updateTransition({ open: false });
    }
  };

  const getIDValue = () => {
    if (form.values.IDType === PersonPrimaryIdType.Ssn) {
      return formatSocialSecurity(form.values.IDValue);
    }

    if (form.values.IDType === PersonPrimaryIdType.Dl) {
      return `${form.values.IDState}-${form.values.IDValue}`;
    }

    return form.values.IDValue;
  };

  const handleRegistration = form.submit(async () => {
    updateTransition({ open: true });
    //If ID available for the customer. Map the ID with a person identified using name, dob etc
    if (isUserWithoutExistingID || isNewIDRegistration) {
      try {
        const isBusinessRegistration =
          form.values.registrationType === OnlineRegistrationType.Commercial &&
          form.values.businessRegistrationType ===
            BUSINESS_REGISTRATION_TYPE.BusinessEntity;

        const validatePersonResponse = await validatePersonForRegistration({
          payload: {
            dateOfBirth: form.values.birthdate
              ? moment(form.values.birthdate).format('YYYY-MM-DD')
              : '',
            firstName: isBusinessRegistration
              ? form.values.businessName
              : form.values.firstName,
            middleName: isBusinessRegistration ? '' : form.values.middleName,
            lastName: isBusinessRegistration ? '' : form.values.lastName,
            ...(form.values.primaryAccountOption === 'accountNumber'
              ? { accountId: form.values.accountNumber }
              : { phoneNumber: form.values.phoneNumber }),
          },
        });

        const personId =
          validatePersonResponse?.data?.validatePersonForRegistration?.personId;

        if (personId) {
          const registerIDResponse = await registerIDDetails({
            variables: {
              payload: {
                personId,
                personalIdentification: [
                  {
                    type: form.values.IDType,
                    value: getIDValue(),
                  },
                ],
              },
            },
          });

          if (registerIDResponse.data?.updatePersonIdValueDetails?.isSuccess) {
            await registrationOnlineUser();
          } else {
            handleError(t('REGISTRATION_ERROR_UNABLE_TO_SAVE_THE_ID_DETAILS'));
            errorCount.current += 1;
            if (errorCount.current > 2) {
              void navigate(ROUTES.REGISTRATION_SPECIAL_HANDLING);
            }
          }
        } else {
          handleError(t('REGISTRATION_ERROR_UNABLE_TO_LOCATE_PERSON'));
          errorCount.current += 1;
          if (errorCount.current > 2) {
            void navigate(ROUTES.REGISTRATION_SPECIAL_HANDLING);
          }
        }
      } catch (e) {
        showCallForAssitanceBanner();
      }
    } else {
      await registrationOnlineUser();
    }

    updateTransition({ open: false });
  });

  const showCallForAssitanceBanner = () => {
    return handleError(
      richT('REGISTRATION_ERROR_CALL_FOR_ASSISTANCE', {
        CUSTOMER_SERVICE_NUMBER: `tel:${t(
          'CALL_FOR_ASSISTANCE_NUMBER_REGISTRATION',
        )}`,
      }),
    );
  };

  function createGroupName({
    firstName,
    middleName,
    lastName,
    businessName,
    registrationType,
    businessRegistrationType,
  }: Model) {
    if (registrationType === OnlineRegistrationType.Residential) {
      return '';
    }

    if (
      businessRegistrationType === BUSINESS_REGISTRATION_TYPE.UserForBusiness
    ) {
      const nameArray = middleName
        ? [firstName, middleName, lastName]
        : [firstName, lastName];
      return nameArray.join(' ');
    }
    return businessName || '';
  }

  return {
    ...form,
    handleSubmit,
    handleRegistrationWithUserData,
  };
}
